/**
 * F17 T17.1.5 — StartNode: węzeł startowy procesu.
 *
 * Renderuje zielony okrąg z ikoną Play; własna nazwa węzła (`data.label`, jeśli ustawiona)
 * stoi NAD okręgiem — pod spodem jest uchwyt wyjścia i strzałka do następnego etapu, a Start
 * nigdy nie dostaje plakietki położenia sprawy (te stoją nad innymi klockami). Tylko jeden Start per graf
 * (walidacja publish). Handle: source bottom (do następnego node'a).
 *
 * `data.isCurrent` (F17 T17.1.8c) — podświetlenie w read-only `ProcessGraphViewer`. Builder
 * nigdy tego pola nie ustawia (patrz `graphTypes.ts`), więc render w builderze bez zmian.
 */

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { Play } from 'lucide-react';

import { CURRENT_NODE_RING_CLASS } from '@/lib/processes/step-tone';

interface StartNodeData {
  label?: string;
  isCurrent?: boolean;
  [key: string]: unknown;
}

export const StartNode = memo(function StartNode({ data, selected }: NodeProps) {
  const { t } = useTranslation();
  const nodeData = (data as StartNodeData) ?? {};
  const name = nodeData.label?.trim() ?? '';
  const isCurrent = nodeData.isCurrent ?? false;
  return (
    <div
      className={`relative flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/20 transition-shadow ${
        selected ? 'ring-2 ring-accent-500 ring-offset-2 dark:ring-offset-gray-900' : ''
      } ${isCurrent ? CURRENT_NODE_RING_CLASS : ''}`}
      data-testid="process-node-start"
      data-current={isCurrent ? 'true' : 'false'}
    >
      {name && (
        <span
          className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 max-w-[180px] -translate-x-1/2 truncate whitespace-nowrap text-xs font-medium text-text-primary"
          data-testid="process-node-start-name"
          title={name}
        >
          {name}
        </span>
      )}
      <Play className="ml-0.5 h-7 w-7 text-white" />
      {isCurrent && <span className="sr-only">{t('tenant.processes.flow.currentStepAria')}</span>}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-3 !w-3 !border-2 !border-white !bg-emerald-600"
      />
    </div>
  );
});
